import { useState } from 'react';
import { calc, editDetails, inr, isValidLocal, toInstant, toLocal, type ItemInput } from '@nandanam/domain';
import { useStore } from '../app/store';
import { Field, Modal } from '../ui/components';
import { ErrorBox, ViewOnlyNote, useSubmit } from './kit';

/** Change the customer's name, the date and times, package, guests or occasion of an enquiry, hold or upcoming booking. */
export function EditOverlay({ id }: { id: string }) {
  const S = useStore(), r = S.rec(id), st = S.state.settings;
  const [items, setItems] = useState<ItemInput[]>(() => (r ? r.items.map(i => ({ pkg: i.pkg, start: i.start, end: i.end })) : []));
  const [guests, setGuests] = useState<number | ''>(r?.guests ?? '');
  const [type, setType] = useState(r?.type ?? '');
  const [name, setName] = useState(r ? S.cust(r.cust).name : '');
  const sub = useSubmit();
  if (!r) return null;
  const setItem = (n: number, p: Partial<ItemInput>) => setItems(xs => xs.map((x, i) => (i === n ? { ...x, ...p } : x)));
  // Moving the start carries the end along, keeping the same length, so the end never lands before the start.
  const setStart = (n: number, start: string) => setItems(xs => xs.map((x, i) => {
    if (i !== n) return x;
    if (!isValidLocal(start) || !isValidLocal(x.start) || !isValidLocal(x.end)) return { ...x, start };
    const len = Math.max(0, toInstant(x.end) - toInstant(x.start));
    return { ...x, start, end: toLocal(toInstant(start) + len) };
  }));
  const patch = { items, guests: Number(guests || 0), type };

  // Preview through the same domain function the save uses.
  const oldName = S.cust(r.cust).name, nameChanged = name.trim() !== oldName;
  const others = S.state.records.filter(x => x.cust === r.cust && x.id !== r.id).length;
  let problem: string | null = !name.trim() ? 'Enter the name' : guests === '' || Number(guests) <= 0 ? 'Enter the number of guests' : null, warnings: string[] = [];
  let after = r;
  if (!problem) {
    try { const res = editDetails(r, patch, S.state, { now: S.now, settings: st, actor: S.account.name }); after = res.value; warnings = res.warnings; }
    catch (e) { problem = e instanceof Error ? e.message : String(e); }
  }
  const before = calc(r), now = calc(after), detailsChanged = after !== r, changed = detailsChanged || nameChanged;
  const save = () => sub.run(async () => {
    if (nameChanged) {
      const x = await S.act('customer.update', `customer:${r.cust}`, { id: r.cust, patch: { name: name.trim() } }, detailsChanged ? undefined : 'Name updated');
      if (!x.ok) return x;
    }
    if (detailsChanged) {
      const x = await S.act('record.edit', `record:${id}`, { id, patch }, 'Details updated');
      if (!x.ok) return x;
    }
    S.close();
    return { ok: true };
  });

  return (
    <Modal size="lg" kicker="Edit" title="Edit details" sub={`${S.num(r)} · ${S.cust(r.cust).name}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!!problem || !changed || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Save changes'}</button></>}>
      <ViewOnlyNote />
      <Field label="Booking name">
        <input className="input" value={name} onChange={e => setName(e.target.value)} />
      </Field>
      {nameChanged && others > 0 && <div className="muted" style={{ fontSize: 12.5, marginTop: -4 }}>This name is also used on {others} other booking{others > 1 ? 's' : ''} of this customer, which will show the new name too.</div>}
      {items.map((it, n) => {
        const kind = r.items[n]!.kind;
        const pkgs = st.packages.filter(p => p.kind === kind && (p.active || p.id === it.pkg));
        return (
          <div key={n} className="card tight" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Field label={kind === 'event' ? 'Event package' : 'Stay package'}>
              <select className="select" value={it.pkg} onChange={e => setItem(n, { pkg: e.target.value })}>{pkgs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            </Field>
            <div className="grid2">
              <Field label={kind === 'event' ? 'Starts' : 'Check-in'}><input type="datetime-local" className="input" value={it.start} onChange={e => e.target.value && setStart(n, e.target.value)} /></Field>
              <Field label={kind === 'event' ? 'Ends' : 'Check-out'}><input type="datetime-local" className="input" value={it.end} min={it.start} onChange={e => e.target.value && setItem(n, { end: e.target.value })} /></Field>
            </div>
          </div>
        );
      })}
      <div className="grid2">
        <Field label="Guests"><input type="number" inputMode="numeric" min={1} className="input" value={guests} onChange={e => setGuests(e.target.value === '' ? '' : Math.max(0, Math.round(Number(e.target.value))))} /></Field>
        {r.kind !== 'stay' && <Field label="Occasion"><select className="select" value={type} onChange={e => setType(e.target.value)}>{[...new Set([type, ...st.eventTypes])].map(t => <option key={t} value={t}>{t}</option>)}</select></Field>}
      </div>
      {detailsChanged && !problem && (
        <div className="kv card tight">
          <div><span>Total</span><span>{now.pending ? 'To be agreed' : before.total === now.total ? `${inr(now.total)} (unchanged)` : `${inr(before.total)} → ${inr(now.total)}`}</span></div>
          {before.dRequired !== now.dRequired && <div><span>Caution deposit</span><span>{inr(before.dRequired)} → {inr(now.dRequired)}</span></div>}
        </div>
      )}
      {detailsChanged && !problem && r.items.some((i, n) => (i.agreed !== i.std || i.custom) && after.items[n]!.agreed === i.agreed && after.items[n] !== i) && <div className="muted" style={{ fontSize: 12.5 }}>The price you agreed is kept. Use Adjust pricing if it should change.</div>}
      {warnings.map(w => <div key={w} className="warn-box">{w}</div>)}
      {problem && changed !== undefined && <div className="error-box">{problem}</div>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}
